import { mayShowInConversation, nextDeliveryState } from '@agion/mission-stream'
import type { MessageDelivery } from '../../hooks/useAnswerDelivery'
import type { StreamingChatMessage } from '../../hooks/useStreamingMissionChat'

/**
 * A follow-up typed after a run has ended launches a new execution. The
 * streaming hook resets its message list for every execution, so the earlier
 * conversation and the text the user just typed have to be carried by the
 * chat component itself. Agion#2064.
 *
 * The earlier run's messages that can show in the conversation are carried;
 * the placement table (@agion/mission-stream messagePlacement.ts) decides
 * which those are.
 */

export interface FollowUpAttachment {
  filename: string
  content_type: string
  size: number
}

export interface BuildCarriedTranscriptInput {
  missionId: string
  /** Messages already carried from earlier runs in this view. */
  carried: StreamingChatMessage[]
  /** Live messages of the run that just ended. */
  previous: StreamingChatMessage[]
  /** Text the user typed to start the new run. */
  followUpText?: string
  attachments?: FollowUpAttachment[]
  /** Divider text shown between the earlier run and the new one. */
  dividerText: string
  now: number
  /**
   * The launch message's delivery while its launch request is on its way
   * (phase 4, agion/Agion#2327). Left out, the message carries none.
   */
  delivery?: MessageDelivery
}

/** The id of the launch message of the launch at `now`. */
function launchMessageId(now: number): string {
  return `launch-msg-${now}`
}

const RUN_DIVIDER_PREFIX = 'run-divider-'

/** The id of the divider that the launch at `now` puts before its run. */
function runDividerId(now: number): string {
  return `${RUN_DIVIDER_PREFIX}${now}`
}

/**
 * The carried rows of the run that started last: those after its divider, or
 * every carried row when no earlier conversation was carried (then only its
 * launch message is). Earlier runs' rows are not this run's.
 */
export function currentRunCarried<T extends StreamingChatMessage>(carried: readonly T[]): T[] {
  let start = 0
  carried.forEach((m, i) => {
    if (m.id.startsWith(RUN_DIVIDER_PREFIX)) start = i + 1
  })
  return carried.slice(start)
}

/**
 * Returns the messages to keep on screen when a new run starts: the earlier
 * conversation, a divider when there was one, and the user's launch message.
 * The launch message is delivered to the mission through the launch input,
 * so it is display-only here.
 */
export function buildCarriedTranscript(input: BuildCarriedTranscriptInput): StreamingChatMessage[] {
  const { missionId, carried, previous, followUpText, attachments, dividerText, now, delivery } = input
  const earlier = [...carried, ...previous.filter((m) => mayShowInConversation(m))]
  const text = followUpText?.trim() ?? ''
  const hasLaunchMessage = text.length > 0 || (attachments?.length ?? 0) > 0

  const next: StreamingChatMessage[] = [...earlier]
  if (earlier.length > 0) {
    next.push({
      id: runDividerId(now),
      type: 'system_notification',
      timestamp: now,
      missionId,
      data: { message: dividerText, severity: 'info' },
    })
  }
  if (hasLaunchMessage) {
    next.push({
      id: launchMessageId(now),
      type: 'hitl_response',
      timestamp: now,
      missionId,
      data: {
        response: text,
        attachments: attachments?.length
          ? attachments.map((a) => ({ filename: a.filename, contentType: a.content_type, size: a.size }))
          : undefined,
        ...(delivery ? { delivery, deliveryOrigin: 'composer' } : {}),
        // The member's own launch message (agion/Agion#2039).
        sentByThisChat: true,
      },
    })
  }
  return next
}

/** How a launch request ended: its run started, or it failed with a reason. */
export type LaunchOutcome = { started: true } | { started: false; reason: string }

/**
 * The carried transcript once the launch at `now` has an outcome (phase 4,
 * agion/Agion#2327). The launch request is the send of its message: a started
 * run is its receipt, so the message is delivered. A failed request leaves the
 * message failed with the reason, and its text and files stay in the
 * composer; the divider that announced a new run goes, because none started.
 */
export function settleLaunch(messages: StreamingChatMessage[], now: number, outcome: LaunchOutcome): StreamingChatMessage[] {
  const id = launchMessageId(now)
  const settled = messages.map((m) => {
    const before = (m.data as { delivery?: MessageDelivery }).delivery
    if (m.id !== id || !before) return m
    const state = nextDeliveryState(before.state, outcome.started ? 'receipt' : 'not_sent')
    const delivery: MessageDelivery = outcome.started
      ? { state }
      : { state, failure: 'not_started', detail: outcome.reason }
    return { ...m, data: { ...m.data, delivery } }
  })
  return outcome.started ? settled : settled.filter((m) => m.id !== runDividerId(now))
}

/** The stored turns of one earlier run of a held conversation. */
export interface EarlierRun {
  executionId: string
  messages: StreamingChatMessage[]
}

/**
 * The earlier runs of a conversation an operator holds (agion/Agion#3435),
 * as one transcript to show before the held run: each run's messages that can
 * show in the conversation, oldest run first, each followed by the divider a
 * live chat puts between runs. A row already shown, by id, is shown once; a
 * run with nothing new to show adds no divider.
 */
export function earlierRunsTranscript(runs: readonly EarlierRun[], missionId: string, dividerText: string): StreamingChatMessage[] {
  const seen = new Set<string>()
  const out: StreamingChatMessage[] = []
  for (const run of runs) {
    const rows = run.messages.filter((m) => mayShowInConversation(m) && !seen.has(m.id))
    if (rows.length === 0) continue
    rows.forEach((m) => seen.add(m.id))
    out.push(...rows, {
      id: `${RUN_DIVIDER_PREFIX}earlier-${run.executionId}`,
      type: 'system_notification',
      timestamp: rows[rows.length - 1]!.timestamp,
      missionId,
      data: { message: dividerText, severity: 'info' },
    })
  }
  return out
}

/** messages without a row whose id an earlier one already has. */
export function withoutRepeatedRows<T extends { id: string }>(messages: readonly T[]): T[] {
  const seen = new Set<string>()
  return messages.filter((m) => {
    if (seen.has(m.id)) return false
    seen.add(m.id)
    return true
  })
}
